import * as React from "react";
import { useState, useCallback, useMemo } from "react";
import { StyleSheet, View } from "react-native";
import { useHistory } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";

import {
  Button,
  Panel,
  Typography,
  VStack,
  useTheme,
  ConfirmDialog,
} from "@smartrent/ui";
import { ChevronLeft, ChevronRight } from "@smartrent/icons";
import { useModalState } from "@smartrent/hooks";
import type { AxiosError } from "axios";

import { useQueryClient } from "@tanstack/react-query";

import { useNetsuiteSubmitOrderMutation } from "@/api";
import { useUser, useGlobalContext } from "@/layout/Context";
import type { NetsuiteDraftResponseData } from "@/types";

import type { NetsuiteFormValues as FormValues } from "./types";
import {
  SHIPPING_METHODS,
  DEFAULT_SHIPPING_METHOD_SLUG,
} from "./shipping-methods";

import { NetsuiteSelectDevicesForm } from "./form-steps/NetsuiteSelectDevicesForm";
import {
  NetsuiteContactInfoForm,
  contactInfoValidationSchema,
} from "./form-steps/NetsuiteContactInfoForm";
import { NetsuiteShippingForm } from "./form-steps/NetsuiteShippingForm";
import { getShippingInfoValidationSchema } from "./form-steps/shippingInfoValidationSchema";
import {
  NetsuiteSummaryForm,
  RETRYABLE_ERROR,
} from "./form-steps/NetsuiteSummaryForm";

const getInitialValues = (groupId?: number): FormValues => ({
  group_id: groupId,
  devices: [],
  subtotal: 0,
  isSubtotalOverLimit: false,
  contact_info_management_company: "",
  contact_info_property: "",
  contact_info_name: "",
  contact_info_email: "",
  contact_info_phone: "",
  shipping_address_company: "",
  shipping_address_property: "",
  shipping_address_line1: "",
  shipping_address_city: "",
  shipping_address_state: "",
  shipping_address_zip: "",
  shipping_address_contact_name: "",
  shipping_address_contact_phone: "",
  shipping_address_contact_email: "",
  shipping_method_slug: DEFAULT_SHIPPING_METHOD_SLUG,
  is_custom_shipping_address: false,
  termsAccepted: false,
});

const steps = [
  { key: "devices", title: "Select Devices" },
  { key: "contact", title: "Contact Info" },
  { key: "shipping", title: "Shipping Address" },
  { key: "summary", title: "Summary" },
];

const STEPS = {
  SELECT_DEVICES: 0,
  CONTACT_INFO: 1,
  SHIPPING_FORM: 2,
  SUMMARY: 3,
} as const;

interface NetsuiteHardwareOrderFormStepsProps {
  groupId?: number;
}

export const NetsuiteHardwareOrderFormSteps: React.FC<
  NetsuiteHardwareOrderFormStepsProps
> = ({ groupId }) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const user = useUser();
  const { setToast } = useGlobalContext();
  const { colors } = useTheme();

  const [currentStep, setCurrentStep] = useState(0);
  const { visible, onOpen, onClose } = useModalState();
  const [pendingValues, setPendingValues] = useState<FormValues | null>(null);

  const initialValues = useMemo(() => getInitialValues(groupId), [groupId]);
  const [shippingAddressError, setShippingAddressError] = useState<
    string | null
  >(null);
  const [submitRetryCount, setSubmitRetryCount] = useState(0);
  const [isDraftLoading, setIsDraftLoading] = useState(false);
  const [isDraftError, setIsDraftError] = useState(false);
  const [draftResponse, setDraftResponse] = useState<
    NetsuiteDraftResponseData | undefined
  >(undefined);

  const handleDraftStateChange = useCallback(
    ({
      isLoading,
      isError,
      draftData,
    }: {
      isLoading: boolean;
      isError: boolean;
      draftData: NetsuiteDraftResponseData | undefined;
    }) => {
      setIsDraftLoading(isLoading);
      setIsDraftError(isError);
      setDraftResponse(draftData);
    },
    []
  );

  const getBackUrl = useCallback(() => {
    if (groupId) {
      return `/groups/${groupId}/hardware-orders`;
    }
    return "/hardware-orders";
  }, [groupId]);

  const [submitOrder, { isLoading: isSubmitting }] =
    useNetsuiteSubmitOrderMutation();

  const getCurrentValidationSchema = useCallback(() => {
    switch (currentStep) {
      case STEPS.SELECT_DEVICES:
        return Yup.object().shape({
          devices: Yup.array()
            .min(1, "Select at least one device")
            .required("Select at least one device"),
          subtotal: Yup.number()
            .max(5000, "Order subtotal cannot exceed $5,000")
            .required(),
        });
      case STEPS.CONTACT_INFO:
        return contactInfoValidationSchema;
      case STEPS.SHIPPING_FORM:
        return getShippingInfoValidationSchema(SHIPPING_METHODS);
      default:
        return Yup.object();
    }
  }, [currentStep]);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep]);

  const handleBack = useCallback(() => {
    if (currentStep > STEPS.SELECT_DEVICES) {
      if (currentStep === steps.length - 1) {
        setShippingAddressError(null);
        setSubmitRetryCount(0);
      }
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, setShippingAddressError, setSubmitRetryCount]);

  const submitOrderHandler = useCallback(
    async (values: FormValues) => {
      if (!values.netsuiteData || !user.id || !values.shipping_method_slug) {
        setToast({
          type: "error",
          title: "Error",
          message: "Missing required information. Please review your order.",
          timeout: 5000,
        });
        return;
      }

      try {
        const lineItems = values.devices.map((device) => ({
          item_id: parseInt(device.id, 10),
          quantity: device.quantity,
        }));

        await submitOrder({
          group_id: values.group_id!,
          customer_id: values.netsuiteData.customerId,
          transaction_type: "WARRANTY_ORDER",
          purchase_order: values.contact_info_po_number,
          order_confirmation_email: values.shipping_address_contact_email,
          is_custom_shipping_address:
            values.is_custom_shipping_address ?? false,
          online_portal_user: {
            first_name: user.first_name || "",
            last_name: user.last_name || "",
            email: user.email || "",
          },
          contact_info_management_company:
            values.contact_info_management_company,
          contact_info_property: values.contact_info_property,
          contact_info_po_number: values.contact_info_po_number,
          contact_info_name: values.contact_info_name,
          contact_info_email: values.contact_info_email,
          contact_info_phone: values.contact_info_phone,
          shipping_address_company: values.shipping_address_company,
          shipping_address_property: values.shipping_address_property,
          shipping_address_line1: values.shipping_address_line1,
          shipping_address_line2: values.shipping_address_line2,
          shipping_address_city: values.shipping_address_city,
          shipping_address_state: values.shipping_address_state,
          shipping_address_zip: values.shipping_address_zip,
          shipping_address_contact_name: values.shipping_address_contact_name,
          shipping_address_contact_phone: values.shipping_address_contact_phone,
          shipping_address_contact_email: values.shipping_address_contact_email,
          shipping_method_slug: values.shipping_method_slug,
          // Billing same as shipping for warranty orders
          billing_address_company: values.shipping_address_company,
          billing_address_property: values.shipping_address_property,
          billing_address_line1: values.shipping_address_line1,
          billing_address_line2: values.shipping_address_line2,
          billing_address_city: values.shipping_address_city,
          billing_address_state: values.shipping_address_state,
          billing_address_zip: values.shipping_address_zip,
          billing_address_contact_name: values.shipping_address_contact_name,
          billing_address_contact_phone: values.shipping_address_contact_phone,
          billing_address_contact_email: values.shipping_address_contact_email,
          line_items: lineItems,
        });

        setToast({
          type: "success",
          title: "Success",
          message: "Hardware order submitted successfully!",
          timeout: 5000,
        });

        queryClient.invalidateQueries({ queryKey: ["hardware-orders"] });
        history.push(getBackUrl());
      } catch (error) {
        const axiosErr = error as AxiosError<{ errors: string[] }>;
        const errorMessage = axiosErr.response?.data?.errors?.[0];
        if (errorMessage === RETRYABLE_ERROR) {
          setShippingAddressError(errorMessage);
          setSubmitRetryCount((prev) => prev + 1);
        } else {
          setToast({
            type: "error",
            title: "Error",
            message: "Failed to submit order. Please try again.",
            timeout: 5000,
          });
        }
      }
    },
    [
      submitOrder,
      user,
      setToast,
      queryClient,
      history,
      getBackUrl,
      setShippingAddressError,
      setSubmitRetryCount,
    ]
  );

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      if (currentStep < steps.length - 1) {
        handleNext();
        return;
      }

      // Final step - store values and show confirmation modal
      setPendingValues(values);
      onOpen();
    },
    [currentStep, handleNext, onOpen]
  );

  return (
    <View style={styles.container}>
      <Panel>
        <View style={styles.header}>
          <Typography type="title2">New Hardware Order</Typography>
          <Button
            variation="plain"
            size="small"
            onPress={() => history.push(getBackUrl())}
          >
            Cancel
          </Button>
        </View>

        {/* Step Indicator */}
        <View style={styles.stepIndicator}>
          {steps.map((step, index) => (
            <View key={step.key} style={styles.stepItem}>
              <View
                style={[
                  styles.stepCircle,
                  {
                    backgroundColor:
                      index <= currentStep
                        ? colors.primary
                        : colors.borderMedium,
                  },
                ]}
              >
                <Typography
                  type="caption"
                  weight="semibold"
                  style={
                    index <= currentStep
                      ? styles.stepNumberActive
                      : styles.stepNumberInactive
                  }
                >
                  {index + 1}
                </Typography>
              </View>
              <Typography
                type="caption"
                weight={index === currentStep ? "semibold" : "regular"}
              >
                {step.title}
              </Typography>
            </View>
          ))}
        </View>
      </Panel>

      <Formik
        initialValues={initialValues}
        validationSchema={getCurrentValidationSchema()}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={true}
      >
        {({ isValid, submitForm, values }) => (
          <Form>
            <VStack spacing={24}>
              {/* Step Content */}
              <View style={styles.stepContent}>
                {currentStep === STEPS.SELECT_DEVICES && (
                  <NetsuiteSelectDevicesForm groupId={groupId ?? null} />
                )}
                {currentStep === STEPS.CONTACT_INFO && (
                  <NetsuiteContactInfoForm />
                )}
                {currentStep === STEPS.SHIPPING_FORM && (
                  <NetsuiteShippingForm shippingMethods={SHIPPING_METHODS} />
                )}
                {currentStep === STEPS.SUMMARY && (
                  <NetsuiteSummaryForm
                    shippingAddressError={shippingAddressError}
                    submitRetryCount={submitRetryCount}
                    isRetryLoading={isSubmitting}
                    onDraftStateChange={handleDraftStateChange}
                    shippingMethods={SHIPPING_METHODS}
                    onRetry={() => {
                      if (!isSubmitting) {
                        setShippingAddressError(null);
                        void submitOrderHandler(values);
                      }
                    }}
                  />
                )}
              </View>

              {/* Navigation Buttons */}
              <Panel>
                <View style={styles.navigation}>
                  <Button
                    variation="outlined"
                    onPress={handleBack}
                    disabled={currentStep === STEPS.SELECT_DEVICES}
                    iconLeft={ChevronLeft}
                  >
                    Back
                  </Button>

                  {currentStep < steps.length - 1 ? (
                    <Button
                      onPress={submitForm}
                      iconRight={ChevronRight}
                      disabled={
                        currentStep === STEPS.SELECT_DEVICES &&
                        (values.netsuiteData?.iotWarrantyActive === false ||
                          values.isSubtotalOverLimit)
                      }
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      onPress={submitForm}
                      isLoading={isSubmitting}
                      disabled={
                        !isValid ||
                        isSubmitting ||
                        isDraftLoading ||
                        isDraftError ||
                        !draftResponse ||
                        !!shippingAddressError
                      }
                    >
                      Submit Order
                    </Button>
                  )}
                </View>
              </Panel>
            </VStack>
          </Form>
        )}
      </Formik>

      <ConfirmDialog
        title="Confirm Order Submission"
        description="Are you sure you want to submit this hardware order? This action cannot be undone."
        visible={visible}
        onClose={() => {
          onClose();
          setPendingValues(null);
        }}
        onConfirm={async () => {
          onClose();
          if (pendingValues) {
            await submitOrderHandler(pendingValues);
            setPendingValues(null);
          }
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  stepNumberActive: {
    color: "white",
  },
  stepNumberInactive: {
    // Default color from theme
  },
  container: {
    padding: 16,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  stepIndicator: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  stepItem: {
    alignItems: "center",
    flex: 1,
  },
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 8,
  },
  stepContent: {
    marginTop: 16,
  },
  navigation: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 16,
  },
});
